import AccountContext from 'context/AccountContext';
import { useContext, useMemo } from 'react';
import { useParkingStore } from 'stores/parkingStore';
import {
  LicensePlate,
  Maybe,
  PricingProps,
  SegmentProps,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { isPermitForParkingZone } from 'utils/permitUtils';
import useSelectedParkingVehicle from './useSelectedParkingVehicle';

const segmentExists = (segment: Maybe<SegmentProps>): segment is SegmentProps =>
  segment?.uid !== undefined;

const permitGrantsAccessToSegment = (
  permit: Maybe<UnifiedPermit>,
  segmentUid: string | null | undefined
) => permit?.accessToCustomerSegmentUid === segmentUid;

const poolGrantsAccessToSegment = (
  permit: Maybe<UnifiedPermit>,
  segmentUid: string | null | undefined
) =>
  permit?.accessPermitPoolingGroupInfo?.some(
    (group) =>
      group?.customerSegmentInsidePool === segmentUid ||
      group?.customerSegmentOutsidePool === segmentUid
  );

const useGetAvailableCustomerSegments = (
  pricingProps: Maybe<Maybe<PricingProps | undefined>[]> | undefined
) => {
  const {
    customer: { permits, licensePlateBasedPermits },
  } = useContext(AccountContext);

  const { selectedParkingZoneId } = useParkingStore(
    (state) => state.parkingConditions
  );

  const selectedVehicle = useSelectedParkingVehicle();

  const plateMatches = (
    registrationPlates: Maybe<Maybe<LicensePlate>[]> | undefined
  ) =>
    registrationPlates?.some(
      (lp) => lp?.plateText === selectedVehicle?.licensePlate.plateText
    );

  const licensePlateHasPermit = (segment: SegmentProps) =>
    licensePlateBasedPermits?.some(
      (permit) =>
        permitGrantsAccessToSegment(permit, segment.uid) &&
        plateMatches(permit?.registrationPlates) &&
        isPermitForParkingZone(selectedParkingZoneId, permit)
    );

  const userHasAccessToPrivateSegment = (segment: SegmentProps) =>
    permits?.some(
      (permit) =>
        (permitGrantsAccessToSegment(permit, segment.uid) ||
          poolGrantsAccessToSegment(permit, segment.uid)) &&
        isPermitForParkingZone(selectedParkingZoneId, permit) &&
        (permit?.usageType !== 'LICENSE_PLATE' ||
          plateMatches(permit?.registrationPlates))
    );

  const filterSegment = (segment: Maybe<SegmentProps>) =>
    segment?.accessType !== 'PRIVATE' ||
    userHasAccessToPrivateSegment(segment) ||
    licensePlateHasPermit(segment);

  const flatSegments = useMemo(() => {
    if (!pricingProps || pricingProps.length === 0) {
      return [];
    }

    const mapOfSegments = new Map<string, SegmentProps>();

    pricingProps.forEach((pricing) => {
      if (!pricing) {
        return;
      }

      if (pricing.defaultSegment?.uid) {
        mapOfSegments.set(pricing.defaultSegment.uid, pricing.defaultSegment);
      }

      pricing.customerSegments?.forEach((segment) => {
        if (segment?.uid) {
          mapOfSegments.set(segment.uid, segment);
        }
      });
    });

    return Array.from(mapOfSegments.values());
  }, [pricingProps]);

  const availableSegments = useMemo(
    () => flatSegments.filter(filterSegment).filter(segmentExists),
    [flatSegments, selectedVehicle]
  );

  const isPrivateOnlyWithNoAccess = useMemo(
    () =>
      flatSegments.length > 0 &&
      flatSegments.every((s) => s.accessType === 'PRIVATE') &&
      availableSegments.length === 0,
    [flatSegments, availableSegments]
  );

  return {
    availableSegments,
    allSegments: flatSegments,
    isPrivateOnlyWithNoAccess,
  };
};

export default useGetAvailableCustomerSegments;
